import {
  Col,
  Row,
  Space,
  Table,
  Card,
  Spin,
  message,
  Divider,
  Button,
  Typography,
  DatePicker,
  Radio,
  Dropdown,
} from "antd";
import React, { useState, useMemo } from "react";
import {
  PrinterOutlined,
  FilePdfOutlined,
  FileExcelOutlined,
  DownOutlined,
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import {
  HEADERS_EXPORT_BEN,
  POST_REPORT_DAILY_ADVANCE_CLEAR_PAY_DB2,
  POST_REPORT_DAILY_ADVANCE_PAY_DB2,
} from "../../API/apiUrls";
import CurrencyFormat from "../../../hook/CurrencyFormat";
import "../../../assets/font/THSarabunNew-normal";
import "../../../assets/font/THSarabunNew-bold";
import MotionHoc from "../../../utils/MotionHoc";
import { generateAdvancePayReport } from "./report/Advancepayreportpdf";
import { generateClearPayReport } from "./report/Clearpayreportpdf";
import XLSX from "xlsx-js-style";
import ThaiDatePicker from "../../../hook/ThaiDatePicker";

const { Text } = Typography;

// ✅ เพิ่ม _seqNo ใน transformClearData
const transformClearData = (list) => {
  const seen = new Set();
  let seq = 0;

  return list.map((r) => {
    const isFirst = !seen.has(r.DOCNO);
    if (isFirst) {
      seen.add(r.DOCNO);
      seq++;
    }
    return {
      ...r,
      _originalDocno: r.DOCNO,
      _seqNo: seq,
      _isFirst: isFirst,
      DOCNO: isFirst ? r.DOCNO : null,
      DOCDT: isFirst ? r.DOCDT : null,
      NAME_LAWYER: isFirst ? r.NAME_LAWYER : null,
      OFFCODE: isFirst ? r.OFFCODE : null,
      USERID: isFirst ? r.USERID : null,
      ADVPAYAMT: isFirst ? r.ADVPAYAMT : null,
      NETPAY: isFirst ? r.NETPAY : null,
      CHKFLAG: isFirst ? r.CHKFLAG : null,
    };
  });
};

// ── Unique by DOCNO ───────────────────────────────────────
const uniqueByDocno = (list) =>
  Object.values(
    list.reduce((acc, r) => {
      if (!acc[r.DOCNO]) acc[r.DOCNO] = r;
      return acc;
    }, {}),
  );

// ─────────────────────────────────────────────────────────
const Main = () => {
  const [
    currencyFormat,
    currencyFormatComma,
    currencyFormatPoint,
    currencyFormatNoPoint,
  ] = CurrencyFormat();
  const [loading, setLoading] = useState(false);
  const [arrayPay, setArrayPay] = useState([]);
  const [startDate, setStartDate] = useState(dayjs().startOf("month"));
  const [endDate, setEndDate] = useState(dayjs().endOf("month"));
  const [docType, setDocType] = useState("advance");

  // ── Query ───────────────────────────────────────────────
  const onQuery = () => {
    if (!startDate || !endDate) {
      message.warning("กรุณาเลือกวันที่ก่อนค้นหา");
      return;
    }
    queryData(
      {
        firstDate: dayjs(startDate).format("YYYY-MM-DDTHH:mm:ssZ"),
        lastDate: dayjs(endDate).format("YYYY-MM-DDTHH:mm:ssZ"),
      },
      docType === "advance"
        ? POST_REPORT_DAILY_ADVANCE_PAY_DB2
        : POST_REPORT_DAILY_ADVANCE_CLEAR_PAY_DB2,
    );
  };

  const queryData = async (payload, urlApi) => {
    setLoading(true);
    try {
      const res = await axios.post(urlApi, payload, {
        headers: HEADERS_EXPORT_BEN,
      });

      if (res.status === 200) {
        const sorted = (res.data ?? []).sort(
          (a, b) => dayjs(a.INPUTDT).valueOf() - dayjs(b.INPUTDT).valueOf(),
        );
        console.log("sorted", sorted);

        setArrayPay(sorted);
      } else {
        setArrayPay([]);
      }
    } catch (err) {
      message.error(
        err?.response?.status === 404
          ? "ไม่พบข้อมูลในช่วงวันที่ที่ค้นหา"
          : "เกิดข้อผิดพลาดในการดึงข้อมูล",
      );
      setArrayPay([]);
    } finally {
      setLoading(false);
    }
  };

  // ── Print PDF ────────────────────────────────────────────
  const handlePrintPDF = () => {
    if (!arrayPay.length) {
      message.warning("ไม่มีข้อมูลสำหรับพิมพ์");
      return;
    }

    const params = {
      dataList: docType === "clear" ? tableData : arrayPay,
      fromDate: startDate,
      toDate: endDate,
      currencyFormatPoint,
    };

    docType === "advance"
      ? generateAdvancePayReport(params)
      : generateClearPayReport(params);
  };

  const handleExportExcel = () => {
    if (!arrayPay.length) {
      message.warning("ไม่มีข้อมูล");
      return;
    }
    console.log("arrayPay", arrayPay);

    const rows =
      docType === "clear"
        ? arrayPay.map((r, index) => ({
            ลำดับ: index + 1,
            วันที่เอกสาร: r.DOCDT ? dayjs(r.DOCDT).format("DD/MM/YYYY") : "",
            เลขที่เอกสาร: r.DOCNO ?? "",
            รายละเอียด: r.DESCRIPTION || r.MEMO1 || "",
            ผู้เคลียร์: r.NAME_LAWYER
              ? `${r.NAME_LAWYER}${r.OFFCODE ? ` (${r.OFFCODE})` : ""}`
              : r.USERID || "",
            จำนวนเงิน: r.AMOUNT != null ? parseFloat(r.AMOUNT) : 0,
            ยอดเบิก: r.ADVPAYAMT != null ? parseFloat(r.ADVPAYAMT) : 0,
            ผลต่าง: r.NETPAY != null ? parseFloat(r.NETPAY) : 0,
            ประเภท:
              r.CHKFLAG === "1" ? "เงินสด" : r.CHKFLAG === "2" ? "เงินโอน" : "",
          }))
        : arrayPay.map((r, index) => ({
            ลำดับ: index + 1,
            วันที่เอกสาร: r.DOCDT ? dayjs(r.DOCDT).format("DD/MM/YYYY") : "",
            เลขที่เอกสาร: r.DOCNO ?? "",
            ประเภท:
              r.CHKFLAG === "1" ? "เงินสด" : r.CHKFLAG === "2" ? "เงินโอน" : "",
            รายละเอียด: r.MEMO1 ?? "",
            ผู้เบิก: r.NAME_LAWYER
              ? `${r.NAME_LAWYER}${r.OFFCODE ? ` (${r.OFFCODE})` : ""}`
              : r.USERID || "",
            จำนวนเงิน: r.TOTAMT != null ? parseFloat(r.TOTAMT) : 0,
            สถานะ: r.FLAG ?? "N",
          }));

    if (!rows.length) {
      message.warning("ไม่มีข้อมูล");
      return;
    }

    const keys = Object.keys(rows[0]);

    // ── money keys ตาม docType ───────────────────────────
    const moneyKeys =
      docType === "clear" ? ["จำนวนเงิน", "ยอดเบิก", "ผลต่าง"] : ["จำนวนเงิน"];

    // ── summary row ──────────────────────────────────────
    const summaryRow = Object.fromEntries(keys.map((k) => [k, ""]));
    const firstKey = keys[2];
    summaryRow[firstKey] = `รวมทั้งหมด ${rows.length} รายการ`;
    moneyKeys.forEach((k) => {
      summaryRow[k] = rows.reduce((s, r) => s + (Number(r[k]) || 0), 0);
    });

    // ── styles ───────────────────────────────────────────
    const border = {
      top: { style: "thin", color: { rgb: "CCCCCC" } },
      bottom: { style: "thin", color: { rgb: "CCCCCC" } },
      left: { style: "thin", color: { rgb: "CCCCCC" } },
      right: { style: "thin", color: { rgb: "CCCCCC" } },
    };
    const fills = {
      even: { patternType: "solid", fgColor: { rgb: "EBF5FF" } },
      odd: { patternType: "solid", fgColor: { rgb: "FFFFFF" } },
      summary: { patternType: "solid", fgColor: { rgb: "D5E8D4" } },
      header: { patternType: "solid", fgColor: { rgb: "34495E" } },
    };

    const ws = XLSX.utils.json_to_sheet([...rows, summaryRow]);

    // header style
    keys.forEach((_, ci) => {
      const ref = XLSX.utils.encode_cell({ r: 0, c: ci });
      if (!ws[ref]) return;
      ws[ref].s = {
        fill: fills.header,
        font: { bold: true, sz: 14, color: { rgb: "FFFFFF" } },
        alignment: { horizontal: "center", vertical: "center" },
        border,
      };
    });

    // data rows style
    rows.forEach((row, ri) => {
      keys.forEach((key, ci) => {
        const ref = XLSX.utils.encode_cell({ r: ri + 1, c: ci });
        if (!ws[ref]) ws[ref] = { v: "", t: "s" };
        const isMoney = moneyKeys.includes(key);

        // ไฮไลท์ผลต่างติดลบ
        const isNegative = key === "ผลต่าง" && Number(row[key]) < 0;

        ws[ref].s = {
          fill: ri % 2 === 0 ? fills.even : fills.odd,
          font: {
            sz: 14,
            color: isNegative ? { rgb: "E74C3C" } : { rgb: "000000" },
          },
          alignment: {
            horizontal: isMoney ? "right" : "center",
            vertical: "center",
          },
          border,
          numFmt: isMoney ? "#,##0.00" : undefined,
        };
      });
    });

    // summary row style
    const sumIdx = rows.length + 1;
    keys.forEach((key, ci) => {
      const ref = XLSX.utils.encode_cell({ r: sumIdx, c: ci });
      if (!ws[ref]) return;
      const isMoney = moneyKeys.includes(key);
      ws[ref].s = {
        fill: fills.summary,
        font: { bold: true, sz: 14 },
        alignment: {
          horizontal: isMoney ? "right" : "left",
          vertical: "center",
        },
        border,
        numFmt: isMoney ? "#,##0.00" : undefined,
      };
    });

    // column width
    const colWidthMap = {
      ลำดับ: 12,
      วันที่เอกสาร: 20,
      เลขที่เอกสาร: 25,
      ประเภท: 10,
      รายละเอียด: 50,
      ผู้เบิก: 40,
      ผู้เคลียร์: 40,
      จำนวนเงิน: 20,
      ยอดเบิก: 20,
      ผลต่าง: 20,
      สถานะ: 12,
    };
    ws["!cols"] = keys.map((k) => ({ wch: colWidthMap[k] ?? 14 }));

    const sheetName = docType === "clear" ? "เคลียร์เงิน" : "เบิกเงิน";
    const fileName =
      docType === "clear"
        ? `รายงานเคลียร์เงิน_${dayjs().format("DDMMYYYY")}.xlsx`
        : `รายงานเบิกเงินสำรอง_${dayjs().format("DDMMYYYY")}.xlsx`;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    XLSX.writeFile(wb, fileName);
    message.success("Export Excel สำเร็จ");
  };

  // ── Derived data ─────────────────────────────────────────
  const tableData = useMemo(
    () => (docType === "clear" ? transformClearData(arrayPay) : arrayPay),
    [arrayPay, docType],
  );

  const uniquePayList = useMemo(
    () => (docType === "clear" ? uniqueByDocno(arrayPay) : arrayPay),
    [arrayPay, docType],
  );

  // ✅ Map DOCNO → group index สำหรับสีสลับ
  const docnoIndexMap = useMemo(() => {
    const map = {};
    let idx = 0;
    arrayPay.forEach((r) => {
      if (!(r.DOCNO in map)) map[r.DOCNO] = idx++;
    });
    return map;
  }, [arrayPay]);

  // ── Columns ──────────────────────────────────────────────
  const advanceColumns = [
    {
      title: "วันที่เอกสาร",
      dataIndex: "INPUTDT",
      key: "INPUTDT",
      align: "center",
      width: 100,
      sorter: (a, b) => dayjs(a.INPUTDT).valueOf() - dayjs(b.INPUTDT).valueOf(),
      render: (v) => (v ? dayjs(v).format("DD/MM/YYYY") : ""),
    },
    {
      title: "เลขที่เอกสาร",
      dataIndex: "DOCNO",
      key: "DOCNO",
      align: "center",
      width: 130,
      render: (v) => v ?? "",
    },
    {
      title: "ประเภท",
      dataIndex: "CHKFLAG",
      key: "CHKFLAG",
      align: "center",
      width: 80,
      render: (v) => (v === "1" ? "เงินสด" : v === "2" ? "เงินโอน" : ""),
    },
    {
      title: "รายละเอียด",
      dataIndex: "MEMO1",
      key: "MEMO1",
      align: "left",
      width: 220,
      render: (v) => v ?? "",
    },
    {
      title: "ผู้เบิก",
      dataIndex: "NAME_LAWYER",
      key: "NAME_LAWYER",
      align: "left",
      width: 130,
      render: (v, r) =>
        v ? `${v}${r.OFFCODE ? ` (${r.OFFCODE})` : ""}` : r.USERID || "",
    },
    {
      title: "จำนวนเงิน",
      dataIndex: "TOTAMT",
      key: "TOTAMT",
      align: "right",
      width: 100,
      sorter: (a, b) => (a.TOTAMT ?? 0) - (b.TOTAMT ?? 0),
      render: (v) => (v != null ? currencyFormatPoint(v) : ""),
    },
    {
      title: "สถานะ",
      dataIndex: "FLAG",
      key: "FLAG",
      align: "center",
      width: 70,
      render: (v) => (
        <Text
          style={{ color: v === "Y" ? "#000" : "#e74c3c", fontWeight: "bold" }}
        >
          {v ?? "N"}
        </Text>
      ),
    },
  ];

  const clearColumns = [
    {
      title: "ลำดับ",
      key: "seq",
      align: "center",
      width: 60,
      render: (_, r) => (r._isFirst ? r._seqNo : ""),
    },
    {
      title: "วันที่เอกสาร",
      dataIndex: "INPUTDT",
      key: "INPUTDT",
      align: "center",
      width: 100,
      sorter: (a, b) => dayjs(a.INPUTDT).valueOf() - dayjs(b.INPUTDT).valueOf(),
      render: (v) => (v ? dayjs(v).format("DD/MM/YYYY") : ""),
    },
    {
      title: "เลขที่เอกสาร",
      dataIndex: "DOCNO",
      key: "DOCNO",
      align: "center",
      width: 130,
      render: (v) => v ?? "", // ✅ "" แทน "-"
    },
    {
      title: "รายละเอียด",
      dataIndex: "DESCRIPTION",
      key: "DESCRIPTION",
      align: "left",
      width: 200,
      render: (v, r) => v || r.MEMO1 || "",
    },
    {
      title: "ผู้เคลียร์",
      dataIndex: "NAME_LAWYER",
      key: "NAME_LAWYER",
      align: "left",
      width: 130,
      render: (v, r) =>
        v ? `${v}${r.OFFCODE ? ` (${r.OFFCODE})` : ""}` : r.USERID || "", // ✅ "" แทน "-"
    },
    {
      title: "จำนวนเงิน",
      dataIndex: "AMOUNT",
      key: "AMOUNT",
      align: "right",
      width: 100,
      sorter: (a, b) => (a.TOTAMT ?? 0) - (b.TOTAMT ?? 0), // ✅
      render: (v) => (v != null ? currencyFormatPoint(v) : ""),
    },
    {
      title: "ยอดเบิก",
      dataIndex: "ADVPAYAMT",
      key: "ADVPAYAMT",
      align: "right",
      width: 100,
      render: (v) => (v != null ? currencyFormatPoint(v) : ""), // ✅ null → ""
    },
    {
      title: "ผลต่าง",
      dataIndex: "NETPAY",
      key: "NETPAY",
      align: "right",
      width: 100,
      render: (v) => {
        if (v == null) return ""; // ✅ null → "" ป้องกัน NaN
        const n = parseFloat(v);
        if (isNaN(n)) return ""; // ✅ NaN guard
        return (
          <Text
            style={{
              color: n < 0 ? "#e74c3c" : "#000",
              fontWeight: n !== 0 ? "bold" : "normal",
            }}
          >
            {currencyFormatPoint(n)}
          </Text>
        );
      },
    },
    {
      title: "ประเภท",
      dataIndex: "CHKFLAG",
      key: "CHKFLAG",
      align: "center",
      width: 80,
      render: (v) => (v === "1" ? "เงินสด" : v === "2" ? "เงินโอน" : ""), // ✅ "" แทน "-"
    },
  ];

  const exportMenuItems = {
    items: [
      {
        key: "pdf",
        label: "Export PDF",
        icon: <FilePdfOutlined style={{ color: "#c0392b" }} />,
      },
      {
        key: "excel",
        label: "Export Excel",
        icon: <FileExcelOutlined style={{ color: "#27ae60" }} />,
      },
    ],
    onClick: ({ key }) => {
      if (key === "pdf") handlePrintPDF();
      if (key === "excel") handleExportExcel();
    },
  };

  // ── Summary ──────────────────────────────────────────────
  const renderSummary = () => {
    if (!arrayPay.length) return null;

    if (docType === "advance") {
      const cashList = arrayPay.filter((r) => r.CHKFLAG === "1");
      const transferList = arrayPay.filter((r) => r.CHKFLAG !== "1");
      const sumCash = cashList.reduce(
        (s, r) => s + parseFloat(r.TOTAMT ?? 0),
        0,
      );
      const sumTransfer = transferList.reduce(
        (s, r) => s + parseFloat(r.TOTAMT ?? 0),
        0,
      );
      return (
        <>
          {[
            {
              label: "เงินสด",
              count: cashList.length,
              sum: sumCash,
              color: "#f6ffed",
              border: "#b7eb8f",
              text: "#389e0d",
            },
            {
              label: "เงินโอน",
              count: transferList.length,
              sum: sumTransfer,
              color: "#e6f4ff",
              border: "#91caff",
              text: "#1677ff",
            },
          ].map(({ label, count, sum, color, border, text }) => (
            <Table.Summary.Row key={label}>
              <Table.Summary.Cell colSpan={5}>
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    background: color,
                    border: `1px solid ${border}`,
                    borderRadius: 6,
                    padding: "2px 12px",
                  }}
                >
                  <span style={{ color: text, fontWeight: 600, fontSize: 13 }}>
                    รวม{label}
                  </span>
                  <span
                    style={{
                      background: text,
                      color: "#fff",
                      borderRadius: 10,
                      padding: "0 7px",
                      fontSize: 11,
                      fontWeight: 500,
                    }}
                  >
                    {count} รายการ
                  </span>
                </div>
              </Table.Summary.Cell>
              <Table.Summary.Cell align="right">
                <span style={{ color: text, fontWeight: 700, fontSize: 14 }}>
                  {currencyFormatPoint(sum)}
                </span>
              </Table.Summary.Cell>
              <Table.Summary.Cell />
            </Table.Summary.Row>
          ))}
        </>
      );
    }

    const sumAmt = arrayPay.reduce((s, r) => s + r.AMOUNT, 0);

    const sumAdv = uniquePayList.reduce((s, r) => s + r.ADVPAYAMT, 0);

    const sumDiff = uniquePayList.reduce((s, r) => {
      const n = r.NETPAY;
      return s + (n !== 0 ? n : r.ADVPAYAMT - r.TOTPAY);
    }, 0);
    const fmt = (num) =>
      num.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    return (
      <Table.Summary.Row>
        <Table.Summary.Cell colSpan={5}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <span
              style={{
                background: "#e8f0fe",
                border: "1px solid #91caff",
                borderRadius: 6,
                padding: "2px 12px",
                color: "#1677ff",
                fontWeight: 600,
                fontSize: 13,
              }}
            >
              รวมทั้งหมด
            </span>
            <span
              style={{
                background: "#f0f0f0",
                border: "1px solid #d9d9d9",
                borderRadius: 10,
                padding: "0 8px",
                fontSize: 11,
                color: "#555",
              }}
            >
              {uniquePayList.length} เอกสาร
            </span>
            <span
              style={{
                background: "#fff7e6",
                border: "1px solid #ffd591",
                borderRadius: 10,
                padding: "0 8px",
                fontSize: 11,
                color: "#d46b08",
              }}
            >
              {arrayPay.length} รายการ
            </span>
          </div>
        </Table.Summary.Cell>
        {[
          {
            value: sumAmt,
            color: "#389e0d",
            bg: "#f6ffed",
            border: "#b7eb8f",
            label: "จำนวนเงิน",
          },
          {
            value: sumAdv,
            color: "#1677ff",
            bg: "#e6f4ff",
            border: "#91caff",
            label: "ยอดเบิก",
          },
          {
            value: sumDiff,
            color: sumDiff < 0 ? "#e74c3c" : "#389e0d",
            bg: sumDiff < 0 ? "#fff2f0" : "#f6ffed",
            border: sumDiff < 0 ? "#ffccc7" : "#b7eb8f",
            label: "ผลต่าง",
          },
        ].map(({ value, color, bg, border, label }) => (
          <Table.Summary.Cell key={label} align="right">
            <div
              style={{
                display: "inline-block",
                background: bg,
                border: `1px solid ${border}`,
                borderRadius: 6,
                padding: "1px 10px",
                minWidth: 90,
                textAlign: "right",
              }}
            >
              <span style={{ color, fontWeight: 700, fontSize: 13 }}>
                {value.toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
          </Table.Summary.Cell>
        ))}
        <Table.Summary.Cell />
      </Table.Summary.Row>
    );
  };

  // ── Render ───────────────────────────────────────────────
  return (
    <Spin spinning={loading} size="large" tip="Loading...">
      {/* ✅ CSS สีสลับตาม DOCNO group */}
      <style>{`
        .row-group-even td { background-color: #ffffff !important; }
        .row-group-odd  td { background-color: #f0f7ff !important; }
      `}</style>

      <Row>
        <Col span={16}>
          <Space style={{ marginBottom: 10 }}>
            <Radio.Group
              value={docType}
              onChange={(e) => {
                setDocType(e.target.value);
                setArrayPay([]);
              }}
              buttonStyle="solid"
            >
              <Radio.Button value="advance">ตั้งเบิกทดรอง</Radio.Button>
              <Radio.Button value="clear">เคลียร์เงินทดรอง</Radio.Button>
            </Radio.Group>
            เริ่ม
            <ThaiDatePicker
              size="large"
              value={startDate}
              onChange={setStartDate}
              placeholder={"วันที่เริ่มต้น"}
              allowClear={false}
            />
            ถึง
            <ThaiDatePicker
              size="large"
              value={endDate}
              onChange={setEndDate}
              placeholder={"วันที่สิ้นสุด"}
              allowClear={false}
            />
            <Button size="large" type="primary" onClick={onQuery}>
              ค้นหา
            </Button>
          </Space>
        </Col>
        <Col span={8} style={{ textAlign: "end" }}>
          {/* <Space style={{ marginBottom: 10 }}>
            <Button
              size="large"
              icon={<PrinterOutlined />}
              onClick={handlePrintPDF}
              disabled={!arrayPay.length}
            >
              พิมพ์
            </Button>
          </Space> */}
          <Space style={{ marginBottom: 10 }}>
            {/* ✅ Dropdown เลือก PDF / Excel */}
            <Dropdown menu={exportMenuItems} disabled={!arrayPay.length}>
              <Button size="large" icon={<PrinterOutlined />}>
                พิมพ์ / Export <DownOutlined />
              </Button>
            </Dropdown>
          </Space>
        </Col>
      </Row>

      <Card style={{ marginBottom: 10 }}>
        <Divider style={{ fontSize: 20 }}>
          {docType === "advance"
            ? "รายงานประจำวันตั้งเบิกทดรอง"
            : "รายงานประจำวันเคลียร์ทดรอง"}
        </Divider>

        {startDate && endDate && (
          <Typography.Text
            style={{
              display: "block",
              textAlign: "center",
              fontSize: 16,
              marginBottom: 8,
            }}
          >
            จากวันที่ {dayjs(startDate).format("DD/MM/YYYY")} ถึงวันที่{" "}
            {dayjs(endDate).format("DD/MM/YYYY")}
          </Typography.Text>
        )}

        <Table
          style={{ marginTop: 10 }}
          size="small"
          columns={docType === "advance" ? advanceColumns : clearColumns}
          dataSource={tableData}
          rowKey={(r, i) => `${r._originalDocno || r.DOCNO || "row"}-${i}`}
          scroll={{ x: "max-content" }}
          pagination={{
            defaultPageSize: 50,
            showSizeChanger: true,
            showTotal: (t) => `ทั้งหมด ${t} รายการ`,
            pageSizeOptions: ["10", "20", "50", "100"],
          }}
          // ✅ สีสลับตาม DOCNO group
          rowClassName={(r) => {
            const docno = r._originalDocno ?? r.DOCNO;
            const idx = docnoIndexMap[docno] ?? 0;
            return idx % 2 === 0 ? "row-group-even" : "row-group-odd";
          }}
          summary={renderSummary}
        />
      </Card>
    </Spin>
  );
};

const MainReportPayment = MotionHoc(Main);
export default MainReportPayment;
